import {
  DestroyRef,
  Directive,
  ElementRef,
  Renderer2,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { coerceNumber } from '@tvd/cdk/utils';
import type { StatusVariant } from '@shared/models/core';
import {
  endWith,
  fromEvent,
  interval,
  map,
  merge,
  takeUntil,
  takeWhile,
} from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Directive({
  selector: '[zynHoldable]',
  host: {
    class: 'relative ',
  },
})
export class HoldableDirective {
  readonly #defaultDuration = 1000;
  readonly holdDuration = input(this.#defaultDuration, {
    alias: 'zynHoldable',
    transform: (val) => coerceNumber(val) || this.#defaultDuration,
  });

  readonly variant = input<StatusVariant>('error');
  readonly held = output();

  readonly #el = inject(ElementRef<HTMLElement>).nativeElement;
  readonly #renderer = inject(Renderer2);
  readonly #destroyRef = inject(DestroyRef);

  #overlayEl?: HTMLElement;
  constructor() {
    effect(() => {
      if (this.#overlayEl) {
        this.#renderer.removeChild(this.#el, this.#overlayEl);
        this.#overlayEl = undefined;
      }
      const overlay = this.#renderer.createElement('div');
      this.#overlayEl = overlay;
      const classList = [
        'absolute',
        'left-0',
        'top-0',
        'h-full',
        'w-0',
        'z-10',
        'max-w-full',
        'text-transparent',
        'mix-blend-multiply',
      ];
      for (const className of classList) {
        this.#renderer.addClass(overlay, className);
      }
      const color = `var(--zyn-${this.variant()})`;
      this.#renderer.setStyle(overlay, 'background-color', color);
      this.#renderer.setStyle(overlay, 'width.%', '0');
      this.#renderer.appendChild(this.#el, overlay);

      const leave$ = fromEvent(this.#el, 'pointerleave').pipe(
        takeUntilDestroyed(this.#destroyRef),
      );

      const down$ = fromEvent(this.#el, 'pointerdown');

      const up$ = fromEvent(this.#el, 'pointerup');

      const hold$ = down$
        .pipe(
          switchMap(() => {
            const startTime = Date.now();
            return interval(16).pipe(
              map(() => Date.now() - startTime),
              takeWhile((elapsed) => elapsed < this.holdDuration(), true),
              tap((elapsed) => {
                if (elapsed >= this.holdDuration()) {
                  this.held.emit();
                }
              }),
              takeUntil(merge(up$, leave$)),
              endWith(0),
            );
          }),
          map((elapsed) =>
            Math.min(100, (elapsed * 100) / this.holdDuration()),
          ),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe((width) => {
          this.#renderer.setStyle(overlay, 'width', `${width}%`);
        });
    });
  }
}
